import React, { useCallback, useEffect, useState } from "react";
import { Users, UserPlus, X, Crown, Loader2, Clock, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Lot 607 — carte « Gestionnaires de la page » (responsable de l'église uniquement) : inviter par e-mail ou numéro
// WhatsApp (invitation à accepter), annuler une invitation, retirer un gestionnaire. Max côté serveur (3).
const Row = ({ u, right, badge, testId }) => (
  <li className="flex items-center gap-3 py-2" data-testid={testId}>
    <Avatar className="h-9 w-9"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u?.name?.[0]}</AvatarFallback></Avatar>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium truncate">{u?.name}</div>
      {badge && <div className="text-[11px] flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>{badge}</div>}
    </div>
    {right}
  </li>
);

export const ChurchManagersCard = ({ church }) => {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [ident, setIdent] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/churches/${church.id}/managers`).then(({ data }) => setData(data)).catch(() => setData(null)), [church.id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const invite = async () => {
    if (!ident.trim()) return;
    setBusy(true);
    try {
      await api.post(`/churches/${church.id}/managers/invite`, { identifier: ident.trim() });
      toast.success(t("church_mgr.invited", "Invitation envoyée. Le membre doit l'accepter."));
      setIdent("");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const cancel = async (iid) => {
    try { await api.post(`/churches/${church.id}/managers/invites/${iid}/cancel`); toast.success(t("church_mgr.invite_cancelled", "Invitation annulée.")); load(); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const remove = async (uid) => {
    if (!window.confirm(t("church_mgr.remove_confirm", "Retirer ce gestionnaire ? Il ne pourra plus modifier la page ni publier au nom de l'église."))) return;
    try { await api.delete(`/churches/${church.id}/managers/${uid}`); toast.success(t("church_mgr.removed", "Gestionnaire retiré.")); load(); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  // Lot 610 — relancer une invitation refusée (7 j après) ou expirée (14 j sans réponse).
  const reinvite = async (iid) => {
    setBusy(true);
    try { await api.post(`/churches/${church.id}/managers/invites/${iid}/reinvite`); toast.success(t("church_mgr.reinvited", "Nouvelle invitation envoyée.")); load(); }
    catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(lang || "fr", { day: "numeric", month: "short" }); } catch { return ""; } };
  const full = data.slots_left <= 0;
  const past = data.past || [];

  return (
    <div className="yalola-card p-5" data-testid="church-managers-card">
      <div className="flex items-center gap-2 mb-1">
        <Users size={17} style={{ color: "var(--yalola-primary)" }} />
        <h2 className="font-bold font-display">{t("church_mgr.title", "Gestionnaires de la page")}</h2>
        <span className="chip text-[10px] ml-auto" data-testid="church-managers-count">{data.managers.length + data.invites.length}/{data.max}</span>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>{t("church_mgr.desc", "Confie la gestion de la page à des membres de confiance : ils pourront modifier la présentation, publier des annonces, des publications et des directs au nom de l'église et voir les statistiques. Les offrandes et la liste des gestionnaires restent réservées au responsable.")}</p>
      <ul className="divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid="church-managers-list">
        {data.owner && <Row u={data.owner} testId="church-manager-owner" badge={<><Crown size={11} /> {t("church_mgr.owner", "Responsable")}</>} />}
        {data.managers.map((m) => (
          <Row key={m.id} u={m} testId={`church-manager-${m.id}`} badge={t("church_mgr.manager", "Gestionnaire")}
               right={<Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => remove(m.id)} data-testid={`church-manager-remove-${m.id}`}><X size={13} className="mr-1" />{t("church_mgr.remove", "Retirer")}</Button>} />
        ))}
        {data.invites.map((inv) => (
          <Row key={inv.id} u={inv.invitee} testId={`church-manager-invite-${inv.id}`} badge={<><Clock size={11} /> {t("church_mgr.pending", "Invitation en attente")}</>}
               right={<Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => cancel(inv.id)} data-testid={`church-manager-invite-cancel-${inv.id}`}><X size={13} className="mr-1" />{t("church_mgr.cancel", "Annuler")}</Button>} />
        ))}
      </ul>
      {!full ? (
        <form className="flex flex-col sm:flex-row gap-2 mt-3" onSubmit={(e) => { e.preventDefault(); invite(); }} data-testid="church-manager-invite-form">
          <Input value={ident} onChange={(e) => setIdent(e.target.value)} placeholder={t("church_mgr.ident_ph", "E-mail ou numéro WhatsApp du membre (ex. +243…)")} inputMode="email" autoComplete="off" data-testid="church-manager-identifier" />
          <Button type="submit" className="rounded-full pill-primary shrink-0" disabled={busy || !ident.trim()} data-testid="church-manager-invite-btn">
            {busy ? <Loader2 size={15} className="animate-spin mr-1" /> : <UserPlus size={15} className="mr-1" />} {t("church_mgr.invite", "Inviter")}
          </Button>
        </form>
      ) : (
        <p className="text-xs mt-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-manager-full">{t("church_mgr.full", "Nombre maximum de gestionnaires atteint ({n}).").replace("{n}", String(data.max))}</p>
      )}
      {past.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid="church-manager-past">
          <div className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><RotateCcw size={12} /> {t("church_mgr.past_title", "Invitations passées")}</div>
          <ul className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
            {past.map((p) => {
              const hint = !p.can_reinvite && (full ? t("church_mgr.reinvite_full", "Plus de place : retire un gestionnaire ou annule une invitation.") : t("church_mgr.reinvite_after", "Possible à partir du {date}").replace("{date}", fmtDate(p.reinvite_at)));
              return (
                <Row key={p.id} u={p.invitee} testId={`church-manager-past-${p.id}`}
                     badge={<span data-testid={`church-manager-past-status-${p.id}`} data-status={p.status}>{p.status === "declined" ? t("church_mgr.past_declined", "A refusé le {date}").replace("{date}", fmtDate(p.responded_at)) : t("church_mgr.past_expired", "Sans réponse — invitation expirée")}{hint && <span className="block" data-testid={`church-manager-past-hint-${p.id}`}>{hint}</span>}</span>}
                     right={<Button size="sm" variant="outline" className="rounded-full text-xs shrink-0" disabled={busy || !p.can_reinvite} onClick={() => reinvite(p.id)} data-testid={`church-manager-reinvite-${p.id}`}><RotateCcw size={13} className="mr-1" />{t("church_mgr.reinvite", "Ré-inviter")}</Button>} />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// Bandeau pour le membre invité (sur la page de l'église) : accepter / refuser.
export const ChurchManagerInviteBanner = ({ church, onDone }) => {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  const inv = church?.my_manager_invite;
  if (!inv) return null;
  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post(`/church-manager-invites/${inv.id}/respond`, { accept });
      toast.success(accept ? t("church_mgr.accepted_toast", "Tu gères maintenant cette page d'église.") : t("church_mgr.declined_toast", "Invitation refusée."));
      onDone?.();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  return (
    <div className="yalola-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" style={{ borderColor: "var(--yalola-primary)" }} data-testid="church-manager-invite-banner">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{t("church_mgr.banner_title", "{name} t'invite à gérer cette page").replace("{name}", inv.inviter_name || t("church_mgr.owner", "Responsable"))}</div>
        <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("church_mgr.banner_desc", "Tu pourras modifier la présentation, publier des annonces et des directs au nom de l'église et voir ses statistiques.")}</div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => respond(false)} data-testid="church-manager-invite-decline">{t("church_mgr.decline", "Refuser")}</Button>
        <Button className="rounded-full pill-primary" disabled={busy} onClick={() => respond(true)} data-testid="church-manager-invite-accept">{t("church_mgr.accept", "Accepter")}</Button>
      </div>
    </div>
  );
};

export default ChurchManagersCard;
